import { computed, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { firstValueFrom, map, Observable, tap } from 'rxjs';

import { CatalogOperationsFacade } from '../../../Facades/catalog-operations.facade';
import { MsgService } from '../../Shared/Services/msg.service';
import { ApplicationDemandsService } from '../Services/application-demands.service';
import { GroupDemandsService } from '../Services/group-demands.service';
import { DemandCreatedToastService } from '../Services/demand-created-toast.service';
import {
  ApplicationDemandDetail,
  ApplicationDemandDetailResponse,
} from '../../../Interfaces/api/applications/demand-detail.interface';
import {
  GroupDemandDetail,
  GroupDemandDetailResponse,
} from '../../../Interfaces/api/groups/demand-detail.interface';
import { DemandCancelData } from '../../../Interfaces/api/applications/demand.interface';
import { DemandSubjectType } from '../../../Enums/demand-subject-type.enum';

@Injectable()
export class MyDemandDetailFacade {
  private readonly _appSvc = inject(ApplicationDemandsService);
  private readonly _groupSvc = inject(GroupDemandsService);
  private readonly _catalogOperations = inject(CatalogOperationsFacade);
  private readonly _msg = inject(MsgService);
  private readonly _createdToast = inject(DemandCreatedToastService);

  private readonly _demandId = signal<string>('');
  private readonly _subjectType = signal<DemandSubjectType>(DemandSubjectType.Application);

  readonly demandId = computed(() => this._demandId());
  readonly subjectType = computed(() => this._subjectType());

  readonly applicationDemand = httpResource<ApplicationDemandDetailResponse>(() => {
    if (this._subjectType() !== DemandSubjectType.Application || !this._demandId()) {
      return undefined;
    }
    return this._appSvc.show(this._demandId());
  });

  readonly groupDemand = httpResource<GroupDemandDetailResponse>(() => {
    if (this._subjectType() !== DemandSubjectType.Group || !this._demandId()) {
      return undefined;
    }
    return this._groupSvc.show(this._demandId());
  });

  readonly loading = computed<boolean>(
    () => this.applicationDemand.isLoading() || this.groupDemand.isLoading(),
  );

  readonly demand = computed<ApplicationDemandDetail | GroupDemandDetail | null>(() => {
    if (this._subjectType() === DemandSubjectType.Application) {
      return this.applicationDemand.hasValue() ? this.applicationDemand.value().data : null;
    }
    return this.groupDemand.hasValue() ? this.groupDemand.value().data : null;
  });

  readonly notFound = computed<boolean>(() => {
    const err =
      this._subjectType() === DemandSubjectType.Application
        ? this.applicationDemand.error()
        : this.groupDemand.error();
    if (!err) return false;
    const status = (err as { status?: number }).status;
    return status === 404 || status === undefined;
  });

  readonly loadFailed = computed<boolean>(() => {
    const err =
      this._subjectType() === DemandSubjectType.Application
        ? this.applicationDemand.error()
        : this.groupDemand.error();
    return !!err && !this.notFound();
  });

  setRoute(subjectType: DemandSubjectType, demandId: string): void {
    this._subjectType.set(subjectType);
    this._demandId.set(demandId);
  }

  reset(): void {
    this._demandId.set('');
  }

  reload(): void {
    if (!this._demandId()) return;
    if (this._subjectType() === DemandSubjectType.Application) {
      this.applicationDemand.reload();
    } else {
      this.groupDemand.reload();
    }
  }

  cancel(): Observable<DemandCancelData> {
    const id = this._demandId();
    const isApp = this._subjectType() === DemandSubjectType.Application;
    const cancel$ = isApp ? this._appSvc.cancel(id) : this._groupSvc.cancel(id);
    return cancel$.pipe(
      tap((response) => {
        if (isApp) {
          this.applicationDemand.reload();
        } else {
          this.groupDemand.reload();
        }
        this._catalogOperations.reload();
        this._msg.showSnackBar(response.message, { type: 'success' });
      }),
      map((response) => response.data),
    );
  }

  async requestAgain(deviceIntuneId?: string): Promise<void> {
    const d = this.demand();
    if (!d || d.subject_type !== 'application') return;
    const applicationId = (d as ApplicationDemandDetail).application.id;
    if (!applicationId) return;

    const payload = deviceIntuneId ? { device_intune_id: deviceIntuneId } : {};
    try {
      const response = await firstValueFrom(this._appSvc.store(applicationId, payload));
      this.applicationDemand.reload();
      this._catalogOperations.reload();
      this._createdToast.announce(response);
    } catch {
      this._msg.showSnackBar('app.catalog.demandDetail.error.requestAgainFailed', {
        type: 'error',
      });
    }
  }
}
